import { useEffect, useState } from "react";
import axios from "axios";
import { Users, IndianRupee, Activity } from "lucide-react";
import StatCard from "../components/StatCard";
import { PageHeader, SectionHeader } from "../components/SectionHeader";
import { useFilters } from "../context/FilterContext";
import { buildDashboardParams } from "../utils/dashboardFilters";
import RAGBadge from "../components/RAGBadge";
import ThemeChip from "../components/ThemeChip";
import { GranteeTable } from "../components/grantee/GranteeTable";
import { POWorkload } from "../components/grantee/POWorkload";
import { MeasTracking } from "../components/grantee/MeasTracking";
import { IndicatorDetails } from "../components/grantee/IndicatorDetails";
import { ChevronDown } from "lucide-react";

import { fmtCr } from "../utils/format";

export default function GranteeOverview() {
  const { filters, filterOptions, viewMode, setGlobalLoading, API } = useFilters();
  const [summary, setSummary] = useState(null);
  const [granteeList, setGranteeList] = useState({ data: [], total: 0 });
  const [indicators, setIndicators] = useState({ data: [], total: 0 });
  const [page, setPage] = useState(1);
  const [indPage, setIndPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [topPerformers, setTopPerformers] = useState({ top: [], needs_attention: [] });
  const [selectedGrantee, setSelectedGrantee] = useState(null);
  const [expandedGrantees, setExpandedGrantees] = useState({});
  const [expandLoading, setExpandLoading] = useState({});
  const [fyTrend, setFyTrend] = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);
  const [indTypeFilter, setIndTypeFilter] = useState("Activity");
  const [poWorkload, setPoWorkload] = useState([]);
  const [measTracking, setMeasTracking] = useState([]);
  const [pendingExpanded, setPendingExpanded] = useState({});
  const [pendingLoading, setPendingLoading] = useState({});

  const sf = filterOptions.data_sources?.grants || filterOptions.data_available;
  const params = buildDashboardParams(filters, { view_mode: viewMode });

  const togglePendingPO = async (po) => {
    if (pendingExpanded[po] !== undefined) {
      setPendingExpanded(prev => { const n = { ...prev }; delete n[po]; return n; });
      return;
    }
    setPendingLoading(prev => ({ ...prev, [po]: true }));
    try {
      const res = await axios.get(`${API}/grantee/meas-pending-grants`, { params: { po } });
      setPendingExpanded(prev => ({ ...prev, [po]: res.data }));
    } catch (e) { console.error(e); }
    finally { setPendingLoading(prev => ({ ...prev, [po]: false })); }
  };

  const toggleGranteeExpand = async (grantee, grantCount, theme) => {
    if (expandedGrantees[grantee] !== undefined) {
      setExpandedGrantees(prev => { const n = { ...prev }; delete n[grantee]; return n; });
      return;
    }
    if (grantCount < 1) return;
    setExpandLoading(prev => ({ ...prev, [grantee]: true }));
    try {
      const res = await axios.get(`${API}/grantee/grants-detail`, {
        params: { grantee, ...buildDashboardParams(filters, { theme: theme || undefined, status: filters.status || undefined }) },
      });
      setExpandedGrantees(prev => ({ ...prev, [grantee]: res.data }));
    } catch (e) { console.error(e); }
    finally { setExpandLoading(prev => ({ ...prev, [grantee]: false })); }
  };

  useEffect(() => {
    setLoading(true);
    setGlobalLoading(true);
    Promise.all([
      axios.get(`${API}/grantee/summary`, { params }),
      axios.get(`${API}/grantee/list`, { params: { ...params, page, per_page: 20 } }),
      sf ? axios.get(`${API}/grantee/indicators`, { params: { ...params, page: indPage, per_page: 30, indicator_type: indTypeFilter } }) : Promise.resolve({ data: { data: [], total: 0 } }),
      sf ? axios.get(`${API}/grantee/top-performers`, { params }).catch(() => ({ data: { top: [], needs_attention: [] } })) : Promise.resolve({ data: { top: [], needs_attention: [] } }),
      axios.get(`${API}/grantee/po-workload`, { params: buildDashboardParams(filters, { trust: filters.trust }) }).catch(() => ({ data: [] })),
      axios.get(`${API}/grantee/meas-tracking`, { params: buildDashboardParams(filters, { trust: filters.trust, status: filters.status }) }).catch(() => ({ data: [] })),
    ]).then(([sRes, lRes, iRes, tpRes, poRes, mtRes]) => {
      setSummary(sRes.data);
      setGranteeList(lRes.data);
      setIndicators(iRes.data);
      setTopPerformers(tpRes.data);
      setPoWorkload(poRes.data);
      setMeasTracking(mtRes.data);
    }).catch(console.error).finally(() => { setLoading(false); setGlobalLoading(false); });
  }, [filters.fy, filters.theme, filters.trust, filters.status, filters.grantee, viewMode, page, indPage, indTypeFilter]);

  const handleGranteeTrendClick = (grantee) => {
    if (selectedGrantee === grantee) { setSelectedGrantee(null); setFyTrend([]); return; }
    setSelectedGrantee(grantee);
    setTrendLoading(true);
    axios.get(`${API}/grantee/fy-trend`, { params: { grantee } })
      .then(r => setFyTrend(r.data))
      .catch(console.error)
      .finally(() => setTrendLoading(false));
  };

  return (
    <div data-testid="grantee-page" className="space-y-6">
      <PageHeader title="Grantee Overview" subtitle="Grant Health Score, KPI details, targets vs actuals" badge="05" />

      {/* Hero Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard title="Total Grantees" value={summary?.total_grantees} icon={Users} color="red" loading={loading} />
        <StatCard title="Active Grants" value={summary?.active_grants} icon={Activity} color="blue" loading={loading} />
        <StatCard title="Total Disbursed" value={summary?.total_disbursed_cr ? fmtCr(summary.total_disbursed_cr) : null}
          icon={IndianRupee} color="green" loading={loading} />
      </div>

      {/* Health Score Formula Banner */}
      {sf && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="health-score-banner">
          <div className="flex items-start gap-3">
            <Activity className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
            <div>
              <h4 className="text-sm font-semibold text-amber-900 mb-1">Health Score Calculation (Interim Formula)</h4>
              <div className="text-xs text-amber-800 space-y-1">
                <p><strong>Current:</strong> KPI Achievement (57%) + Utilisation Rate (43%)</p>
                <p><strong>If KPI unavailable:</strong> Disbursement Rate (55%) + Utilisation Rate (45%)</p>
                <p className="text-amber-600 mt-2">
                  <strong>RAG Thresholds:</strong> 
                  <span className="inline-flex items-center gap-1 ml-2">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>Green ≥70%
                  </span>
                  <span className="inline-flex items-center gap-1 ml-2">
                    <span className="w-2 h-2 rounded-full bg-amber-400"></span>Amber 35-69%
                  </span>
                  <span className="inline-flex items-center gap-1 ml-2">
                    <span className="w-2 h-2 rounded-full bg-red-500"></span>Red &lt;35%
                  </span>
                </p>
                <p className="text-amber-500 italic mt-1">Final formula (pending): KPI (40%) + Utilisation (35%) + Outreach Mapping (25%)</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Performers / Needs Attention */}
      {sf && (topPerformers.top.length > 0 || topPerformers.needs_attention.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SectionHeader title="Top Performers" subtitle="Highest Grant Health Score">
            <div className="space-y-2" data-testid="top-performers">
              {topPerformers.top.map((r, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-slate-50 last:border-0">
                  <span className="text-xs font-bold text-slate-600 w-4">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-slate-900 truncate">{r.grantee}</div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <ThemeChip theme={r.theme} />
                      <span className="text-xs text-slate-600">{fmtCr(r.allocation_cr)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <RAGBadge status={r.rag} size="sm" />
                    <span className="text-sm font-bold text-slate-700">{r.score}%</span>
                  </div>
                  <button onClick={() => handleGranteeTrendClick(r.grantee)}
                    className="text-slate-500 hover:text-[#006837] transition-colors" title="View FY trend">
                    <ChevronDown size={14} className={selectedGrantee === r.grantee ? "rotate-180" : ""} />
                  </button>
                </div>
              ))}
            </div>
          </SectionHeader>

          <SectionHeader title="Needs Attention" subtitle="Lowest Grant Health Score">
            <div className="space-y-2" data-testid="needs-attention">
              {topPerformers.needs_attention.map((r, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-slate-50 last:border-0">
                  <span className="text-xs font-bold text-slate-600 w-4">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-slate-900 truncate">{r.grantee}</div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <ThemeChip theme={r.theme} />
                      <span className="text-xs text-slate-600">{fmtCr(r.allocation_cr)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <RAGBadge status={r.rag} size="sm" />
                    <span className="text-sm font-bold text-slate-700">{r.score}%</span>
                  </div>
                  <button onClick={() => handleGranteeTrendClick(r.grantee)}
                    className="text-slate-500 hover:text-[#006837] transition-colors" title="View FY trend">
                    <ChevronDown size={14} className={selectedGrantee === r.grantee ? "rotate-180" : ""} />
                  </button>
                </div>
              ))}
            </div>
          </SectionHeader>
        </div>
      )}

      {/* Grantee Table + FY Trend (composed component) */}
      <GranteeTable
        granteeList={granteeList}
        page={page} setPage={setPage}
        expandedGrantees={expandedGrantees}
        expandLoading={expandLoading}
        toggleGranteeExpand={toggleGranteeExpand}
        selectedGrantee={selectedGrantee}
        handleGranteeTrendClick={handleGranteeTrendClick}
        fyTrend={fyTrend}
        trendLoading={trendLoading}
      />

      {/* PO Workload */}
      <POWorkload poWorkload={poWorkload} />

      {/* MEAS Tracking */}
      <MeasTracking
        measTracking={measTracking}
        pendingExpanded={pendingExpanded}
        pendingLoading={pendingLoading}
        togglePendingPO={togglePendingPO}
      />

      {/* Indicator Details */}
      <IndicatorDetails
        sf={sf}
        indicators={indicators}
        indPage={indPage} setIndPage={setIndPage}
        indTypeFilter={indTypeFilter} setIndTypeFilter={setIndTypeFilter}
        API={API}
        filters={filters}
      />
    </div>
  );
}
